import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

// TEMPORARY adoption tooling (tenancy rollout) — mirrors the backend's
// src/tenancy-setup/ module and is deleted with it once every environment
// reports zero unassigned rows.

export type TenancySetupStatus = {
  unassigned: Record<string, number>
  usersWithoutMembership: number
  // Memberships with no role: the holder can reach the tenant but has no
  // permissions in it. Must reach zero before this module can be removed.
  membershipsWithoutRole: number
  // Sanity signals. superadminCount === 0 means nobody can reach any global
  // screen, including this one.
  networkCount: number
  superadminCount: number
  totalUnassigned: number
}

export type TenancySetupResult = {
  tenantId: string
  assigned: Record<string, number>
  membershipsCreated: number
  rolesBackfilled: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export const tenancySetupApi = {
  status: () => request<TenancySetupStatus>('/tenancy-setup/status'),

  // defaultRoleId is required: a membership with no role grants nothing, so
  // bulk-creating role-less memberships would lock every non-superadmin out.
  apply: (tenantId: string, defaultRoleId: string) =>
    request<TenancySetupResult>('/tenancy-setup/apply', {
      method: 'POST',
      body: JSON.stringify({ tenantId, defaultRoleId }),
    }),
}
